// src\components\payments\CheckoutPage.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../firebase/AuthContext";
import { db } from "../../firebase/firebaseServices";
import { doc, getDoc } from "firebase/firestore";
import "./paymentsCss/payments.css";
import "../css/CheckoutPage.css";

/* User-facing modal */
import AlertModal from "./server/alertModal";

import {
  loadRuntimePaymentsConfig,
  loadPayPalSDKDynamic,
  fetchPayPalSubscription,
  mapPayPalToSubPayload,
  writeUserSubscription,
  NextBillingVerifier,
  scheduleNextBillingBackfill,
} from "./PayPalSubscriptionService";

/** -------- config (no hard-coded repeats) -------- */
const PLAN_IMAGE_URL = "https://purehealthnexus.com/assets/images/AI_Nutrition.png";
const HOME_ROUTE = "/home";
const SERVICE = "ui.checkout";

/** -------- tiny, deterministic hash for change detection (FNV-1a) -------- */
function hashStringFNV1a(input = "") {
  let hash = 0x811c9dc5;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash =
      (hash +
        ((hash << 1) +
          (hash << 4) +
          (hash << 7) +
          (hash << 8) +
          (hash << 24))) >>>
      0;
  }
  return (`0000000${hash.toString(16)}`).slice(-8);
}

/** -------- idempotency tracker (TTL ≥ replay window) -------- */
const IDEMPOTENCY_TTL_MS = 15 * 60 * 1000;
const idempotencyCache = new Map(); // key -> expireAt
function tryStartIdempotent(key) {
  const now = Date.now();
  const expiresAt = idempotencyCache.get(key);
  if (expiresAt && expiresAt > now) return false;
  idempotencyCache.set(key, now + IDEMPOTENCY_TTL_MS);
  return true;
}
function markCompleteIdempotent(_key) {
  return true;
}

/** -------- safe event publisher with guardrails -------- */
function publishEvent({ type, payload, ingress = "ui" }) {
  const visitedIncoming = Array.isArray(payload?.visited) ? payload.visited : [];
  if (payload?.source === SERVICE || visitedIncoming.includes(SERVICE)) return;

  const serialized = JSON.stringify(payload ?? {});
  const changeHash = hashStringFNV1a(serialized);
  const idempotencyKey = `${SERVICE}:${type}:${changeHash}`;
  if (!tryStartIdempotent(idempotencyKey)) return;

  const envelope = {
    type,
    source: SERVICE,
    requestId: crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
    traceId:
      payload?.traceId ||
      crypto?.randomUUID?.() ||
      `${Date.now()}-${Math.random()}`,
    visited: [...visitedIncoming, SERVICE],
    hopCount: (payload?.hopCount || 0) + 1,
    publishedAt: new Date().toISOString(),
    idempotencyKey,
    changeHash,
    ingress,
    allowEgress: ["analytics", "telemetry"],
    payload,
  };
  try {
    const event = new CustomEvent("phn:event", { detail: envelope });
    window.dispatchEvent(event);
    markCompleteIdempotent(idempotencyKey);
  } catch {
    // breaker → DLQ in a backend implementation
  }
}

export default function CheckoutPage() {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const paypalRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [config, setConfig] = useState({ apiBase: "", paypalClientId: "" });
  const [plan, setPlan] = useState(null);

  /* ---- user-visible alert modal state ---- */
  const [userAlert, setUserAlert] = useState({
    open: false,
    title: "",
    message: "",
    variant: "info",
  });

  const closeUserAlertAndMaybeGoHome = (goHome) => {
    setUserAlert((state) => ({ ...state, open: false }));
    if (goHome) navigate(HOME_ROUTE, { replace: true });
  };

  // Derived: classify error (never recompute unnecessarily)
  const isMissingPlan = useMemo(
    () => /Missing planId/i.test(err || ""),
    [err]
  );

  // Resolve plan (dynamic). Expected shape: { planId, name?, displayName?, price?, currency?, billingPeriodMonths? }
  useEffect(() => {
    let isAlive = true;

    (async () => {
      try {
        const uid = currentUser?.uid || null;

        const runtimeConfig = await loadRuntimePaymentsConfig(db, uid);

        const fromState = location.state?.plan || null;

        let fromSession = null;
        try {
          fromSession = JSON.parse(
            sessionStorage.getItem("phn_checkout_plan") || "null"
          );
        } catch {
          fromSession = null;
        }

        let fromUserDoc = null;
        if (!fromState && !fromSession && uid) {
          try {
            const userSnapshot = await getDoc(doc(db, "users", uid));
            if (userSnapshot.exists()) {
              const user = userSnapshot.data() || {};
              fromUserDoc =
                user.pendingCheckout?.plan || user.checkout?.plan || null;
            }
          } catch {
            fromUserDoc = null;
          }
        }

        const chosenPlan = fromState || fromSession || fromUserDoc;
        if (!chosenPlan?.planId) {
          throw new Error(
            "Missing planId. Open checkout from the Pricing page so it can pass the plan details."
          );
        }

        if (!isAlive) return;
        setConfig(runtimeConfig);
        setPlan(chosenPlan);
        sessionStorage.setItem(
          "phn_checkout_plan",
          JSON.stringify(chosenPlan)
        );
      } catch (error) {
        if (!isAlive) return;
        const message = error?.message || String(error);

        publishEvent({
          type: "checkout.error",
          payload: {
            code: /Missing planId/i.test(message)
              ? "missing_plan"
              : "generic_error",
            message,
            entityId: currentUser?.uid || "anonymous",
          },
          ingress: "ui",
        });

        setErr(message);
        setUserAlert({
          open: true,
          title: "Checkout issue",
          message:
            "We could not prepare checkout. Please start from the Pricing page and try again.",
          variant: "warning",
        });
      } finally {
        if (isAlive) setLoading(false);
      }
    })();

    return () => {
      isAlive = false;
    };
  }, [currentUser?.uid, location.state]);

  // Auto-redirect ONLY for the specific error (Never Loop + idempotent)
  useEffect(() => {
    if (!isMissingPlan) return;
    const redirectedFlagKey = "phn_checkout_autosent_home";
    if (sessionStorage.getItem(redirectedFlagKey) === "1") return;
    sessionStorage.setItem(redirectedFlagKey, "1");
    navigate(HOME_ROUTE, {
      replace: true,
      state: { reason: "missing_plan", from: SERVICE },
    });
  }, [isMissingPlan, navigate]);

  // Render PayPal Button once ready
  useEffect(() => {
    if (loading || err || !config.paypalClientId || !plan || !paypalRef.current) {
      return;
    }

    let mounted = true;
    const hostElement = paypalRef.current;
    hostElement.innerHTML = "";

    loadPayPalSDKDynamic(config.paypalClientId)
      .then((paypal) => {
        if (!mounted) return;

        paypal
          .Buttons({
            createSubscription: (_data, actions) =>
              actions.subscription.create({
                plan_id: String(plan.planId),
              }),

            onApprove: async (data) => {
              try {
                const subscriptionIdentifier =
                  data.subscriptionID || data.subscriptionId || data.orderID;

                const json = await fetchPayPalSubscription(
                  config.apiBase,
                  subscriptionIdentifier
                );

                const subscriptionPayload = mapPayPalToSubPayload(json, {
                  planId: plan.planId,
                  displayName: plan.displayName || plan.name,
                  name: plan.name,
                  price: plan.price,
                  currency: plan.currency,
                  billingPeriodMonths: plan.billingPeriodMonths ?? null,
                });

                let writeResult = { anyActive: true };
                if (currentUser?.uid) {
                  writeResult = await writeUserSubscription(
                    db,
                    currentUser.uid,
                    subscriptionPayload,
                    {
                      lastEventId: "client_onApprove_fetch_success",
                      idempotencyKey: `onApprove:${subscriptionPayload.subscriptionId}`,
                    }
                  );
                }

                let verifierStarted = false;
                const startVerifier = (payloadWithNext) => {
                  if (
                    verifierStarted ||
                    !currentUser?.uid ||
                    !payloadWithNext?.nextBillingTime
                  ) {
                    return;
                  }
                  verifierStarted = true;
                  const iso =
                    payloadWithNext.nextBillingTime.toDate().toISOString();
                  const localSubId = payloadWithNext.subscriptionId;
                  const planMetaLocal = {
                    planId: plan.planId,
                    displayName: plan.displayName || plan.name,
                    name: plan.name,
                    price: plan.price,
                    currency: plan.currency,
                    billingPeriodMonths: plan.billingPeriodMonths ?? null,
                  };
                  const verifier = new NextBillingVerifier({
                    iso,
                    fetchFreshJson: () =>
                      fetchPayPalSubscription(config.apiBase, localSubId),
                    persistFresh: async (freshJson) => {
                      const freshPayload = mapPayPalToSubPayload(
                        freshJson,
                        planMetaLocal
                      );
                      await writeUserSubscription(
                        db,
                        currentUser.uid,
                        freshPayload,
                        {
                          lastEventId: "client_nextBilling_verify",
                          idempotencyKey: `verifyAtNextBill:${
                            freshPayload.subscriptionId
                          }:${
                            freshPayload.statusUpdatedAt?.toMillis?.() || "na"
                          }`,
                        }
                      );
                    },
                  });
                  verifier.start();
                };

                if (subscriptionPayload?.nextBillingTime) {
                  startVerifier(subscriptionPayload);
                } else if (currentUser?.uid) {
                  scheduleNextBillingBackfill({
                    db,
                    uid: currentUser.uid,
                    apiBase: config.apiBase,
                    planMeta: {
                      planId: plan.planId,
                      displayName: plan.displayName || plan.name,
                      name: plan.name,
                      price: plan.price,
                      currency: plan.currency,
                      billingPeriodMonths: plan.billingPeriodMonths ?? null,
                    },
                    initialPayload: subscriptionPayload,
                    onFoundNextBilling: startVerifier,
                  });
                }

                if (!writeResult.anyActive) {
                  navigate(HOME_ROUTE, { replace: true });
                  return;
                }

                navigate(
                  `/checkout/success?subId=${encodeURIComponent(
                    subscriptionPayload.subscriptionId
                  )}&planId=${encodeURIComponent(
                    subscriptionPayload.planId || ""
                  )}`,
                  {
                    replace: true,
                    state: {
                      name: plan.displayName || plan.name,
                      price: plan.price,
                      currency: plan.currency,
                      billingPeriodMonths: plan.billingPeriodMonths ?? null,
                    },
                  }
                );
              } catch (error) {
                setUserAlert({
                  open: true,
                  title: "We could not complete your subscription",
                  message:
                    "Something went wrong before checkout could complete. No payment was taken. Please try again.",
                  variant: "warning",
                });
                publishEvent({
                  type: "checkout.user_notice",
                  payload: {
                    entityId: currentUser?.uid || "anonymous",
                    code: "post_approve_verify_failed",
                    message: String(error?.message || error),
                  },
                  ingress: "ui",
                });
              }
            },

            onError: (error) => {
              const errorMessage = error?.message || String(error);
              setErr(errorMessage);
              setUserAlert({
                open: true,
                title: "We could not start the subscription",
                message:
                  "Please try again. If the problem continues, check your network or try a different browser.",
                variant: "error",
              });
              publishEvent({
                type: "checkout.user_notice",
                payload: {
                  entityId: currentUser?.uid || "anonymous",
                  code: "paypal_button_error",
                  message: String(errorMessage),
                },
                ingress: "ui",
              });
            },
          })
          .render(hostElement)
          .catch((error) => {
            const errorMessage = error?.message || String(error);
            setErr(errorMessage);
            setUserAlert({
              open: true,
              title: "Something went wrong",
              message:
                "We could not load the PayPal button. Please refresh and try again.",
              variant: "error",
            });
          });
      })
      .catch((error) => {
        const errorMessage = error?.message || String(error);
        setErr(errorMessage);
        setUserAlert({
          open: true,
          title: "Payments are temporarily unavailable",
          message:
            "We could not load our payment provider. Please refresh the page or try again later.",
          variant: "error",
        });
      });

    return () => {
      mounted = false;
    };
  }, [
    config.paypalClientId,
    config.apiBase,
    plan,
    loading,
    err,
    currentUser?.uid,
    navigate,
  ]);

  // ---- UI ----
  if (loading) {
    return (
      <div className="checkout-page">
        <main className="checkout-grid">
          <section className="panel">
            <h2>Loading checkout…</h2>
          </section>
        </main>
      </div>
    );
  }

  if (err) {
    return (
      <div className="checkout-page">
        <main className="checkout-grid">
          <section className="panel">
            <h2>Checkout Error</h2>
            <p className="error">{err}</p>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginTop: 12,
              }}
            >
              <button
                type="button"
                onClick={() =>
                  navigate(HOME_ROUTE, {
                    replace: true,
                    state: { reason: "error_click", from: SERVICE },
                  })
                }
                className="btn"
                aria-label="Go to home"
              >
                Go home
              </button>
              <small className="muted">
                or{" "}
                <Link to={HOME_ROUTE} aria-label="Home (link)">
                  home
                </Link>
              </small>
            </div>

            <AlertModal
              open={userAlert.open}
              title={userAlert.title}
              message={userAlert.message}
              variant={userAlert.variant}
              requester={{ entityId: currentUser?.uid || "anonymous" }}
              secondaryAction={{
                label: "Close",
                onClick: () => closeUserAlertAndMaybeGoHome(false),
              }}
              primaryAction={{
                label: "Go to Home",
                onClick: () => closeUserAlertAndMaybeGoHome(true),
              }}
              onClose={() => closeUserAlertAndMaybeGoHome(false)}
            />
          </section>
        </main>
      </div>
    );
  }

  // price is the actual billing amount (30 / 150 / 240)
  const isNumericPrice = typeof plan?.price === "number";
  const currencyCode = plan?.currency || "USD";

  const priceText =
    isNumericPrice && currencyCode
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: currencyCode,
        }).format(plan.price)
      : isNumericPrice
      ? `$${plan.price.toFixed(2)}`
      : "—";

  const billingMonths =
    typeof plan?.billingPeriodMonths === "number" &&
    plan.billingPeriodMonths > 0
      ? plan.billingPeriodMonths
      : 1;

  const effectivePerMonth =
    billingMonths > 1 && isNumericPrice
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: currencyCode,
        }).format(plan.price / billingMonths)
      : null;

  return (
    <div className="checkout-page">
      <main className="checkout-grid">
        <section className="panel">
          <h2>{plan?.displayName || plan?.name || "Your plan"} Checkout</h2>
          <p>Please complete your purchase using the secure PayPal button below.</p>

          <img
            src={PLAN_IMAGE_URL}
            alt="AI Nutrition plan"
            style={{
              display: "block",
              margin: "0 auto 16px",
              width: "100%",
              maxWidth: 520,
              height: "auto",
              borderRadius: 12,
              filter: "none",
              opacity: 1,
              mixBlendMode: "normal",
              boxShadow: "none",
              background: "transparent",
              isolation: "isolate",
            }}
            onError={(event) => {
              const element = event.currentTarget;
              element.src =
                "data:image/svg+xml;utf8," +
                encodeURIComponent(
                  `<svg xmlns='http://www.w3.org/2000/svg' width='800' height='400'>
                     <rect width='100%' height='100%' fill='#efefef'/>
                     <text x='50%' y='50%' font-family='Arial' font-size='20' text-anchor='middle' fill='#888'>
                       Image not found
                     </text>
                   </svg>`
                );
            }}
          />

          <div ref={paypalRef} />

          <div className="powered">Powered by PayPal</div>

          <AlertModal
            open={userAlert.open}
            title={userAlert.title}
            message={userAlert.message}
            variant={userAlert.variant}
            requester={{ entityId: currentUser?.uid || "anonymous" }}
            secondaryAction={{
              label: "Dismiss",
              onClick: () => closeUserAlertAndMaybeGoHome(false),
            }}
            primaryAction={{
              label: "Go to Home",
              onClick: () => closeUserAlertAndMaybeGoHome(true),
            }}
            onClose={() => closeUserAlertAndMaybeGoHome(false)}
          />
        </section>

        <aside className="summary">
          <div className="summary-card">
            <h3>Purchase Details</h3>
            <div className="summary-row">
              <span>Plan:</span>
              <strong>{plan?.displayName || plan?.name || "—"}</strong>
            </div>
            <div className="summary-row">
              <span>Paid with:</span>
              <span>PayPal</span>
            </div>
            <div className="summary-row">
              <span>Receipt sent to:</span>
              <span className="muted">
                {currentUser?.email || "PayPal email"}
              </span>
            </div>
            <div className="summary-row total">
              <span>Total:</span>
              <strong>{priceText}</strong>
            </div>
            {billingMonths > 1 && effectivePerMonth && (
              <div className="summary-row">
                <span>Billing schedule:</span>
                <span>
                  Every {billingMonths} months (approx. {effectivePerMonth}/mo)
                </span>
              </div>
            )}
          </div>
        </aside>
      </main>
    </div>
  );
}
